import {
  ChangeType,
  ComparisonType,
  DateRange,
  VariableType,
} from "@/pages/subscriptions/constants";
import type {
  SubscriptionV2Variable,
  MetricDefinition,
  SubscriptionDetailView,
} from "@/pages/subscriptions/services/types";
import type { EnrichedSubscription } from "../types";

const CHANGE_TYPE_MAP: Record<ChangeType, string> = {
  [ChangeType.RELATIVE_VALUE]: "Relative value",
  [ChangeType.ABSOLUTE_VALUE]: "Absolute value",
};

const COMPARISON_MAP: Record<ComparisonType, string> = {
  [ComparisonType.CHANGES_BY]: "changes by",
  [ComparisonType.EXCEEDS]: "Exceeds",
  [ComparisonType.BELOW]: "below",
  [ComparisonType.CHANGES_BY_PERCENT]: "changes by %",
};

const DATE_RANGE_MAP: Record<DateRange, string> = {
  [DateRange.PREVIOUS_DAY]: "Previous day",
  [DateRange.PREVIOUS_WEEK]: "Previous week",
  [DateRange.PREVIOUS_MONTH]: "Previous month",
  [DateRange.PREVIOUS_YEAR]: "Previous year",
  [DateRange.PREVIOUS_DAY_TRAILING_7_DAY_AVG]: "Trailing 7-day avg",
};

type SubscriptionData = Pick<
  SubscriptionDetailView,
  "id" | "name" | "prompt" | "description" | "variables" | "status" | "rule_type" | "read_only"
>;

export function buildEnrichedSubscription(
  subscription: SubscriptionData,
  metricsData: MetricDefinition[] | undefined,
  categoryDisplayNameLookup: Record<string, string>,
  metricDisplayNameLookup: Record<string, string>
): EnrichedSubscription {
  const metricVariable = subscription.variables.find(v => v.type === VariableType.METRIC);
  const metricName = metricVariable ? String(metricVariable.value) : null;
  const metricData = metricsData?.find(m => m.name === metricName);
  const metricUnit = metricData?.unit || "";

  const enrichedVariables: SubscriptionV2Variable[] = subscription.variables.map(variable => {
    let displayValue = variable.displayValue;

    if (variable.type === VariableType.ENTITY) {
      const category = variable.config?.category || variable.displayValue;
      const categoryDisplayName = categoryDisplayNameLookup[category];
      if (categoryDisplayName) {
        displayValue = categoryDisplayName;
      }
    }

    if (variable.type === VariableType.METRIC && typeof variable.value === "string") {
      const metricDisplayName = metricDisplayNameLookup[variable.value];
      if (metricDisplayName) {
        displayValue = metricDisplayName;
      }
    }

    if (variable.type === VariableType.AGGREGATION) {
      const agg = String(variable.value);
      displayValue = agg.charAt(0).toUpperCase() + agg.slice(1);
    }

    if (variable.type === VariableType.CHANGE_TYPE) {
      const valueStr = String(variable.value) as ChangeType;
      displayValue = CHANGE_TYPE_MAP[valueStr] || valueStr;
    }

    if (variable.type === VariableType.COMPARISON) {
      const valueStr = String(variable.value) as ComparisonType;
      displayValue = COMPARISON_MAP[valueStr] || valueStr;
    }

    if (variable.type === VariableType.DATE_RANGE) {
      const valueStr = String(variable.value) as DateRange;
      displayValue = DATE_RANGE_MAP[valueStr] || valueStr;
    }

    if (variable.type === VariableType.THRESHOLD) {
      if (!variable.displayValue.includes("%") && !variable.displayValue.includes(metricUnit)) {
        const comparisonVar = subscription.variables.find(v => v.type === VariableType.COMPARISON);
        const isPercentage = comparisonVar?.value === ComparisonType.CHANGES_BY_PERCENT;

        if (isPercentage) {
          displayValue = `${variable.displayValue}%`;
        } else if (metricUnit) {
          displayValue = `${variable.displayValue} ${metricUnit}`;
        }
      }
    }

    return { ...variable, displayValue };
  });

  return {
    id: subscription.id,
    name: subscription.name,
    prompt: subscription.prompt,
    description: subscription.description ?? undefined,
    variables: enrichedVariables,
    status: subscription.status,
    rule_type: subscription.rule_type,
    read_only: subscription.read_only ?? false,
  };
}
